import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

// Scroll-driven "template -> custom" expansion (no global wheel hijack, Lenis-safe).
// `background` = the dimmed full-screen template site.
// `foreground` = the custom site that lives inside a small box which expands to full screen.
export default function ScrollExpandMedia({
  title = "",
  scrollToExpand,
  background,
  foreground,
  children,
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  // background (template) fades + slightly scales as the custom box takes over
  const bgOpacity = useTransform(scrollYProgress, [0, 0.55], [1, 0.12]);
  const bgScale = useTransform(scrollYProgress, [0, 0.6], [1, 1.08]);
  const bgSaturate = useTransform(scrollYProgress, [0, 0.5], [1, 0.35]);
  const bgFilter = useTransform(bgSaturate, (v) => `saturate(${v}) brightness(0.95)`);

  // the expanding box (custom) — interpolate NUMBERS then build valid CSS strings
  // (Framer cannot tween a clamp()/max() string directly, so we compute per-frame).
  const wNum = useTransform(scrollYProgress, [0.05, 0.62], [44, 100]);
  const hNum = useTransform(scrollYProgress, [0.05, 0.62], [40, 100]);
  const width = useTransform(wNum, (v) => `max(${v}vw, 300px)`);
  const height = useTransform(hNum, (v) => `max(${v}vh, 220px)`);
  const radius = useTransform(scrollYProgress, [0.05, 0.62], [22, 0]);
  const boxBorder = useTransform(scrollYProgress, [0.45, 0.62], [1, 0]);
  const chromeOpacity = useTransform(scrollYProgress, [0.05, 0.35], [1, 0]);
  const badgeOpacity = useTransform(scrollYProgress, [0.5, 0.62], [1, 0]);

  // title splits apart, then fades
  const tx = useTransform(scrollYProgress, [0, 0.55], [0, 30]); // vw
  const txLeft = useTransform(tx, (v) => `-${v}vw`);
  const txRight = useTransform(tx, (v) => `${v}vw`);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.15, 0.5, 0.62], [0, 1, 1, 0]);

  const hintOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const contentOpacity = useTransform(scrollYProgress, [0.66, 0.82], [0, 1]);
  const contentY = useTransform(scrollYProgress, [0.66, 0.85], [40, 0]);

  const firstWord = title.split(" ")[0] || "";
  const rest = title.split(" ").slice(1).join(" ");

  return (
    <div ref={ref} className="relative h-[280vh]" data-testid="scroll-expand-media">
      <div className="sticky top-0 h-[100svh] overflow-hidden flex items-center justify-center bg-[#05050c]">
        {/* background = template site (dimmed / desaturated) */}
        <motion.div
          style={{ opacity: bgOpacity, scale: bgScale, filter: bgFilter }}
          className="absolute inset-0"
        >
          {background}
          <div className="absolute inset-0 bg-black/20" />
        </motion.div>

        {/* expanding box = custom site */}
        <motion.div
          style={{ width, height, borderRadius: radius, borderWidth: boxBorder }}
          className="relative z-10 overflow-hidden border-teal/40 shadow-glow-teal bg-[#05050c]"
        >
          {/* fake browser chrome that fades away as the box grows */}
          <motion.div
            style={{ opacity: chromeOpacity }}
            className="absolute top-0 inset-x-0 z-30 flex items-center gap-1.5 px-4 h-9 border-b border-white/10 bg-[#0b0b14]"
          >
            <span className="h-2.5 w-2.5 rounded-full bg-magenta" />
            <span className="h-2.5 w-2.5 rounded-full bg-teal" />
            <span className="h-2.5 w-2.5 rounded-full bg-cobalt" />
            <span className="ml-3 h-5 flex-1 rounded-md bg-white/5 flex items-center px-3 text-[10px] text-white/40">
              yourbrand.com
            </span>
          </motion.div>

          {foreground}

          <motion.span
            style={{ opacity: badgeOpacity }}
            className="absolute top-11 left-3 z-30 rounded-full bg-gradient-to-r from-teal to-cobalt px-3 py-1 text-[11px] uppercase tracking-[0.15em] text-black font-semibold"
          >
            Custom-built
          </motion.span>
        </motion.div>

        {/* title splits apart */}
        <motion.div
          style={{ opacity: titleOpacity }}
          className="absolute inset-0 z-20 flex flex-col items-center justify-center pointer-events-none mix-blend-difference"
        >
          <motion.h2 style={{ x: txLeft }} className="font-heading font-extrabold text-white text-4xl md:text-6xl lg:text-7xl">
            {firstWord}
          </motion.h2>
          <motion.h2 style={{ x: txRight }} className="font-heading font-extrabold text-white text-4xl md:text-6xl lg:text-7xl text-center">
            {rest}
          </motion.h2>
        </motion.div>

        {scrollToExpand && (
          <motion.p
            style={{ opacity: hintOpacity }}
            className="absolute bottom-8 z-20 text-xs uppercase tracking-[0.25em] text-white/70"
          >
            {scrollToExpand}
          </motion.p>
        )}

        {/* revealed content once fully expanded */}
        <motion.div
          style={{ opacity: contentOpacity, y: contentY }}
          className="absolute bottom-10 md:bottom-16 z-40 px-6 w-full pointer-events-none"
        >
          {children}
        </motion.div>
      </div>
    </div>
  );
}
